import { Box, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyCard } from "../cards/EmptyCard";
import { WorkerCard } from "../cards/WorkerCard";
import { RouteApp } from "../common/model/RouteApp";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import { RootState } from "../common/redux/store";
import {
    getInterestProfessionnels,
    getInterestStructure,
} from "../professionnels/redux/professionnelAction";
import { DisponibiliteProfessionnel } from "../search/model/Disponibilite";
import { DisponibiliteStructure } from "../structures/model/StructureModel";
import { language, LanguageType } from "../utils/LanguageUtils";

import "./DashboardPage.css";
import styled from "@emotion/styled";
import { ManageStructureCard } from "../cards/ManageStructureCard";
import { ManageProfessionnelCard } from "../cards/ManageProfessionnelCard";
import { StructureCard } from "../cards/StructureCard";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { DashboardSkeleton } from "../common/components/DashboardSkeleton";

export const Dashboard = () => {
    const [loading, setLoading] = useState(true);

    const loadingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const structuresMatchedSelector = useAppSelector(
        (state: RootState) => state.structure
    );

    const professionnelMatchedSelector = useAppSelector(
        (state: RootState) => state.professionnel
    );

    const userSelector = useAppSelector((state: RootState) => state.user);

    const userProSelector = useAppSelector((state: RootState) => state.userPro);

    const dispatch = useAppDispatch();
    const lang: LanguageType = language();
    const { t } = useTranslation();

    useEffect(() => {
        const roles = [
            ...userSelector.data.roles,
            ...userProSelector.data.roles,
        ];

        if (roles.length === 1 && roles[0] === "") return;

        if (roles.includes("ROLE_STRUCTURE")) {
            dispatch(getInterestProfessionnels());
        }

        if (roles.includes("ROLE_PROFESSIONNEL")) {
            dispatch(getInterestStructure());
        }
    }, [dispatch, userSelector.data.roles, userProSelector.data.roles]);

    const personnelsCalled = (props: DisponibiliteProfessionnel, i: number) => {
        return (
            <Grid item key={i} m={1} lg={6}>
                <WorkerCard {...props} key={i} />
            </Grid>
        );
    };

    const companyCalled = (props: DisponibiliteStructure, i: number) => {
        return (
            <Grid item key={i} m={1} lg={6}>
                <StructureCard {...props} key={i} />
            </Grid>
        );
    };

    const structureCalled = (props: DisponibiliteStructure, i: number) => {
        return (
            <Grid item card={6} key={i} lg={9} xs={7}>
                <ManageStructureCard
                    {...props}
                    indexOfDisponibility={i}
                    key={i}
                />
            </Grid>
        );
    };

    const professionnelCalled = (
        props: DisponibiliteProfessionnel,
        i: number
    ) => {
        return (
            <Grid item card={6} key={i} lg={9} xs={7}>
                <ManageProfessionnelCard
                    {...props}
                    indexOfDisponibility={i}
                    key={i}
                />
            </Grid>
        );
    };

    useEffect(() => {
        if (loadingDebounceTimeout.current) {
            clearTimeout(loadingDebounceTimeout.current);
        }

        loadingDebounceTimeout.current = setTimeout(() => {
            if (
                !userSelector.data.isLoader &&
                !userProSelector.data.isLoader &&
                !structuresMatchedSelector.isLoader &&
                !professionnelMatchedSelector.isLoader
            ) {
                setLoading(false);
                return;
            }

            setLoading(true);
        }, 500);
    }, [
        userSelector,
        userProSelector,
        structuresMatchedSelector,
        professionnelMatchedSelector,
    ]);

    if (loading) {
        return <DashboardSkeleton />;
    }

    return (
        <Stack
            sx={{
                width: "100%",
                height: "auto",
            }}
        >
            <ContactContainer
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    mt: 4,
                }}
            >
                <Box
                    sx={{
                        width: "fit-content",
                        gridArea: "card",
                    }}
                >
                    {userSelector.data.id && userSelector.data.societe ? (
                        <Typography
                            variant="h6"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                ml: 2,
                                mt: 3,
                            }}
                        >
                            {t("dashboard.personal.contacted")}
                            <span
                                style={{
                                    backgroundColor: "#3d3d3d",
                                    padding: 6,
                                    color: "white",
                                    borderRadius: "50%",
                                    width: "30px",
                                    height: "30px",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    marginLeft: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {professionnelMatchedSelector.data?.length ?? 0}
                            </span>
                        </Typography>
                    ) : (
                        <Typography
                            variant="h6"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                ml: 2,
                                mt: 3,
                            }}
                        >
                            {t("dashboard.personal.structure")}
                            <span
                                style={{
                                    backgroundColor: "#3d3d3d",
                                    padding: 6,
                                    color: "white",
                                    borderRadius: "50%",
                                    width: "30px",
                                    height: "30px",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    marginLeft: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {structuresMatchedSelector.data?.length ?? 0}
                            </span>
                        </Typography>
                    )}
                    {userSelector.data.id && userSelector.data.societe ? (
                        <ContactCard>
                            {professionnelMatchedSelector.data
                                .filter((_item: any, idx: number) => idx < 3)
                                .map(
                                    (
                                        item: DisponibiliteProfessionnel,
                                        i: number
                                    ) => {
                                        if (item.id && item.id !== "") {
                                            return personnelsCalled(item, i);
                                        }
                                        return null;
                                    }
                                )}
                            {professionnelMatchedSelector.data.length < 3 && (
                                <EmptyCard
                                    message={lang.addPersonnel}
                                    component={RouteApp.SEARCH}
                                ></EmptyCard>
                            )}
                            {professionnelMatchedSelector.data.length > 3 && (
                                <Grid item m={1} lg={6}>
                                    <Link
                                        to={RouteApp.PROFESSIONNELS}
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        (+
                                        {professionnelMatchedSelector.data
                                            .length - 3}
                                        )
                                        <ArrowRightIcon
                                            sx={{
                                                color: "#3d3d3d",
                                                fontSize: "2rem",
                                            }}
                                        />
                                    </Link>
                                </Grid>
                            )}
                        </ContactCard>
                    ) : (
                        <ContactCard>
                            {structuresMatchedSelector.data
                                .filter((_item: any, idx: number) => idx < 3)
                                .map(
                                    (
                                        item: DisponibiliteStructure,
                                        i: number
                                    ) => {
                                        if (item.id && item.id !== "") {
                                            return companyCalled(item, i);
                                        }
                                        return null;
                                    }
                                )}
                            {structuresMatchedSelector.data.length < 3 && (
                                <EmptyCard
                                    message={lang.addStructure}
                                    component={RouteApp.SEARCH}
                                />
                            )}
                            {structuresMatchedSelector.data.length > 3 && (
                                <Grid item m={1} lg={6}>
                                    <Link
                                        to={RouteApp.PROFESSIONNELS}
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        (+
                                        {structuresMatchedSelector.data.length -
                                            3}
                                        )
                                        <ArrowRightIcon
                                            sx={{
                                                color: "#3d3d3d",
                                                fontSize: "2rem",
                                            }}
                                        />
                                    </Link>
                                </Grid>
                            )}
                        </ContactCard>
                    )}
                </Box>
            </ContactContainer>

            <ProjectContainer>
                <Project>
                    <Stack
                        sx={{
                            width: "100%",
                            flexDirection: "row",
                            justifyContent: "space-between",
                        }}
                    >
                        {userSelector.data.id && userSelector.data.societe ? (
                            <Typography
                                variant="h6"
                                sx={{
                                    display: "flex",
                                    mt: 3,
                                    ml: 2,
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                {t("dashboard.projects")}
                                <span
                                    style={{
                                        backgroundColor: "#3d3d3d",
                                        padding: 6,
                                        color: "white",
                                        borderRadius: "50%",
                                        width: "30px",
                                        height: "30px",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        marginLeft: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {userSelector.data.disponibilities.length}
                                </span>
                                <Link
                                    to={RouteApp.MY_PROJECTS}
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    <span
                                        style={{
                                            marginLeft: "10px",
                                            color: "#3d3d3d",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {t("dashboard.viewAll")} →
                                    </span>
                                </Link>
                            </Typography>
                        ) : (
                            <Typography
                                variant="h6"
                                sx={{
                                    display: "flex",
                                    mt: 3,
                                    ml: 2,
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                {t("dashboard.structure")}
                                <span
                                    style={{
                                        backgroundColor: "#3d3d3d",
                                        padding: 6,
                                        color: "white",
                                        borderRadius: "50%",
                                        width: "30px",
                                        height: "30px",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        marginLeft: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {
                                        userProSelector.data.disponibilities
                                            .length
                                    }
                                </span>
                                <Link
                                    to={RouteApp.MY_PROJECTS}
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    <span
                                        style={{
                                            marginLeft: "10px",
                                            color: "#3d3d3d",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {t("dashboard.viewAll")} →
                                    </span>
                                </Link>
                            </Typography>
                        )}
                    </Stack>
                    <Cards>
                        {userSelector.data.societe
                            ? userSelector.data.disponibilities
                                  .slice(0, 3)
                                  .map(
                                      (
                                          item: DisponibiliteStructure,
                                          i: number
                                      ) => structureCalled(item, i)
                                  )
                            : userProSelector.data.disponibilities
                                  .slice(0, 3)
                                  .map(
                                      (
                                          item: DisponibiliteProfessionnel,
                                          i: number
                                      ) => professionnelCalled(item, i)
                                  )}

                        {userSelector.data.societe
                            ? userSelector.data.disponibilities.length < 3 && (
                                  <EmptyCard
                                      message={lang.addProject}
                                      component={RouteApp.MY_PROJECTS}
                                  />
                              )
                            : userProSelector.data.disponibilities.length <
                                  3 && (
                                  <EmptyCard
                                      message={lang.addProject}
                                      component={RouteApp.MY_PROJECTS}
                                  />
                              )}

                        {(userSelector.data.societe &&
                            userSelector.data.disponibilities.length > 3) ||
                        (!userSelector.data.societe &&
                            userProSelector.data.disponibilities.length > 3) ? (
                            <Grid item m={1} lg={6}>
                                <Link
                                    to={RouteApp.MY_PROJECTS}
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                        fontWeight: "bold",
                                    }}
                                >
                                    (+
                                    {(userSelector.data.societe
                                        ? userSelector.data.disponibilities
                                        : userProSelector.data.disponibilities
                                    ).length - 3}
                                    )
                                    <ArrowRightIcon
                                        sx={{
                                            color: "#3d3d3d",
                                            fontSize: "2rem",
                                        }}
                                    />
                                </Link>
                            </Grid>
                        ) : null}
                    </Cards>
                </Project>
            </ProjectContainer>
        </Stack>
    );
};

const ContactContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    margin: "4rem 0 4rem 0",

    "@media(width >= 990px)": {
        alignItems: "start",
    },
});

const ContactCard = styled(Grid)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",

    "@media(width >= 990px)": {
        flexDirection: "row",
        marginLeft: "0.5rem",
    },
});

const ProjectContainer = styled(Box)({
    display: "flex",
    marginTop: "1rem",
    background: "#DCDCDC",
    width: "100%",
    gap: 5,
    margin: "0 0 4rem 0",
    padding: 0,
});

const Project = styled(Box)({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",

    "@media(width >= 990px)": {
        alignItems: "start",
        justifyContent: "center",
        width: "fit-content",
    },
});

const Cards = styled(Stack)({
    width: "100%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "3rem",

    "@media(width >= 990px)": {
        flexDirection: "row",
        justifyContent: "start",
        margin: "0 0 1rem 1rem",
    },
});
